import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@ci-ville/ui';
import { api } from '@/lib/api-client';
const KIND_TAGS = {
    SLA_BREACHED: { label: 'Retard', cls: 'bg-priority-urgent/15 text-priority-urgent' },
    SLA_WARNING: { label: 'Échéance', cls: 'bg-warning/20 text-foreground' },
    TRIAL: { label: 'Essai', cls: 'bg-brand/15 text-brand' },
    RENEWAL: { label: 'Contrat', cls: 'bg-brand/15 text-brand' },
    INVOICE_OVERDUE: { label: 'Facture', cls: 'bg-priority-high/15 text-priority-high' },
    NEGATIVE_FEEDBACK: { label: 'Avis', cls: 'bg-priority-high/15 text-priority-high' },
};
/** Page à ouvrir selon le type d'avis ; les avis de l'équipe GEOSIRA mènent à la fiche de la ville. */
function targetOf(n) {
    if (n.reportId)
        return `/manage?id=${n.reportId}`;
    const p = n.payload ?? {};
    const console = (fiche) => `/super-admin?onglet=villes&ville=${p.tenantId}&fiche=${fiche}`;
    switch (p.kind) {
        case 'TRIAL':
        case 'RENEWAL':
            return p.tenantId ? console('contrats') : '/ma-ville';
        case 'INVOICE_OVERDUE':
            return p.tenantId ? console('factures') : '/ma-ville';
        case 'CLAIMED_REPORTS':
            return '/manage';
        default:
            return null;
    }
}
export function NotificationsBell() {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const drawerRef = useRef(null);
    const { data: countData } = useQuery({
        queryKey: ['notifications-unread-count'],
        queryFn: () => api.notifications.unreadCount(),
        refetchInterval: 60_000, // poll toutes les 60s
        refetchOnWindowFocus: true,
    });
    const { data: notifications = [], isLoading } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.notifications.list(),
        enabled: open,
    });
    const markRead = useMutation({
        mutationFn: (id) => api.notifications.markRead(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
            qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
        },
    });
    const markAllRead = useMutation({
        mutationFn: () => api.notifications.markAllRead(),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['notifications'] });
            qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
        },
    });
    useEffect(() => {
        if (!open)
            return;
        const handler = (e) => {
            if (drawerRef.current && !drawerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);
    const count = countData?.count ?? 0;
    const handleClick = (notif) => {
        if (!notif.readAt)
            markRead.mutate(notif.id);
        const target = targetOf(notif);
        if (target) {
            navigate(target);
            setOpen(false);
        }
    };
    return (_jsxs("div", { ref: drawerRef, className: "relative", children: [_jsxs("button", { onClick: () => setOpen((v) => !v), className: "relative flex size-10 items-center justify-center rounded-md text-white hover:bg-white/15", title: "Notifications", children: [_jsx(Bell, { className: "size-5" }), count > 0 && (_jsx("span", { className: "absolute right-1.5 top-1.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-priority-urgent px-1 text-[9px] font-bold text-white ring-2 ring-brand", children: count > 99 ? '99+' : count }))] }), open && (_jsxs("div", { className: "absolute right-0 top-12 z-[1200] w-96 origin-top-right overflow-hidden rounded-lg border border-border bg-card shadow-xl", children: [_jsxs("div", { className: "flex items-center justify-between border-b border-border bg-muted/30 px-3 py-2", children: [_jsx("h3", { className: "text-sm font-semibold", children: "Notifications" }), count > 0 && (_jsxs(Button, { size: "sm", variant: "ghost", className: "h-7 gap-1 text-xs", onClick: () => markAllRead.mutate(), children: [_jsx(CheckCheck, { className: "size-3.5" }), "Tout marquer lu"] }))] }), _jsx("div", { className: "max-h-96 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center py-8", children: _jsx(Loader2, { className: "size-5 animate-spin text-brand" }) })) : notifications.length === 0 ? (_jsx("p", { className: "py-8 text-center text-xs text-muted-foreground", children: "Aucune notification" })) : (notifications.map((n) => (_jsx("button", { onClick: () => handleClick(n), className: `block w-full border-b border-border/50 px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/50 ${!n.readAt ? 'bg-brand/5' : ''}`, children: _jsxs("div", { className: "flex items-start gap-2", children: [!n.readAt && _jsx("span", { className: "mt-1.5 size-2 shrink-0 rounded-full bg-brand" }), _jsxs("div", { className: `flex-1 ${n.readAt ? 'pl-4' : ''}`, children: [_jsxs("p", { className: "text-xs font-medium text-foreground", children: [KIND_TAGS[n.payload?.kind] && (_jsx("span", { className: `mr-1.5 rounded px-1 py-px text-[9px] font-semibold uppercase ${KIND_TAGS[n.payload.kind].cls}`, children: KIND_TAGS[n.payload.kind].label })), n.subject ?? 'Notification'] }), n.bodyText && (_jsx("p", { className: "mt-0.5 line-clamp-2 text-[11px] text-muted-foreground", children: n.bodyText })), _jsx("p", { className: "mt-1 text-[10px] text-muted-foreground", children: formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: fr }) })] })] }) }, n.id)))) })] }))] }));
}
//# sourceMappingURL=NotificationsBell.js.map